// pages/StorageTransactions.jsx
import { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import CreateStorageTransactionModal from "../components/CreateStorageTransactionModal";
import EditStorageTransactionModal from "../components/EditStorageTransactionModal";
import Select from "react-select";
import { exportToCSV, exportToXLSX } from "../utils/exportStorageTransactions";
import api from '../config/api';


import "../styles/transactions.css";

export default function StorageTransactions() {
  const itemsPerPage = 10;
  const [transactions, setTransactions] = useState([]);
  const [items, setItems] = useState([]);
  const [villas, setVillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedItem, setSelectedItem] = useState("");
  const [selectedVilla, setSelectedVilla] = useState("");
  const [searchDescription, setSearchDescription] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [fetchError, setFetchError] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const handleEdit = (transaction) => {
    setSelectedTransaction(transaction);
    setShowEditModal(true);
  };

  const itemOptions = items.map((item) => ({
    value: item.id,
    label: `${item.code} - ${item.description}`,
  }));

  const filteredTransactions = (transactions || []).filter((tx) => {
    const matchItem = !selectedItem || tx.item_id === Number(selectedItem);
    const matchVilla = !selectedVilla || tx.villa_id === Number(selectedVilla);
    const search = searchDescription.toLowerCase();
    const matchDesc = !searchDescription || (
      (tx.description?.toLowerCase().includes(search)) ||
      (tx.supplier_name?.toLowerCase().includes(search))
    );
        return matchItem && matchVilla && matchDesc;
  });

  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, filteredTransactions.length);
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const fetchTransactions = async () => {
    try {
      const token = localStorage.getItem("token");
      const url = api("/storage-transactions");
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        setFetchError(errorData?.error || "Failed to fetch storage transactions.");
        return;
      }

      const data = await response.json();
      if (!Array.isArray(data)) {
        setFetchError("Invalid response format: expected an array.");
        return;
      }

      const sorted = [...data].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      setTransactions(sorted);
      setFetchError("");
    } catch (err) {
      console.error("Error fetching storage transactions:", err);
      setFetchError("Something went wrong while fetching data.");
    } finally {
      setLoading(false);
    }
  };


  const fetchItems = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(api("/items"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setItems(data);
    } catch (err) {
      console.error("Error fetching items:", err);
    }
  };

  const fetchVillas = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(api("/villas"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setVillas(data);
    } catch (err) {
      console.error("Error fetching villas:", err);
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to permanently delete this storage transaction?");
    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(api(`/storage-transactions/${id}`), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete storage transaction.");
      }

      alert("Storage transaction deleted successfully.");
      fetchTransactions(); // Refresh list
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete storage transaction.");
    }
  };

  const getItemCode = (item_id) => {
    const item = items.find((i) => i.id === item_id);
    return item ? item.code : "-";
  };

  const getVillaName = (villa_id) => {
    const villa = villas.find((v) => v.id === villa_id);
    return villa ? villa.name : "-";
  };

  useEffect(() => {
    fetchTransactions();
    fetchItems();
    fetchVillas();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedItem, selectedVilla, searchDescription]);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <Sidebar />
        <div className="layout-page">
          <Navbar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <h2 className="mb-4">View All Storage Transactions</h2>
              <div className="card mb-4">
                <div className="card-header">
                  <h4>Filter and Search Storage Transactions</h4>
                </div>
                <div className="card-body table-responsive">
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="form-label">Start Date:</label>
                      <input
                        type="date"
                        className="form-control"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">End Date:</label>
                      <input
                        type="date"
                        className="form-control"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4 d-flex align-items-end">
                      <button className="btn btn-primary w-100" onClick={fetchTransactions}>
                        Apply Filter
                      </button>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="form-label">Filter Item:</label>
                      <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        options={[
                          { value: "", label: "All Items" },
                          ...items.map((item) => ({
                            value: item.id,
                            label: `${item.code} - ${item.description}`,
                          })),
                        ]}
                        value={
                          selectedItem === ""
                            ? { value: "", label: "All Items" }
                            : {
                                value: selectedItem,
                                label:
                                  items.find((item) => item.id === Number(selectedItem))?.code +
                                  " - " +
                                  items.find((item) => item.id === Number(selectedItem))?.name,
                              }
                        }
                        onChange={(selected) => setSelectedItem(selected?.value || "")}
                        isClearable
                        placeholder="Search item..."
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Filter Villa:</label>
                      <select
                        className="form-select"
                        value={selectedVilla}
                        onChange={(e) => setSelectedVilla(e.target.value)}
                      >
                        <option value="">All Villas</option>
                        {Array.isArray(villas) && villas.length > 0 ? (
                          villas.map((villa) => (
                            <option key={villa.id} value={villa.id}>
                              {villa.name}
                            </option>
                          ))
                        ) : (
                          <option disabled>No villas available</option>
                        )}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Search Description:</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter description"
                        value={searchDescription}
                        onChange={(e) => setSearchDescription(e.target.value)}
                      />
                    </div>
                  </div>
                  {fetchError && (
                    <div className="alert alert-danger" role="alert">
                        {fetchError}
                    </div>
                    )}
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Item Code</th>
                        <th>Villa</th>
                        <th>Quantity</th>
                        <th>Price/Unit</th>
                        <th>Supplier</th>
                        <th>Transaction Type</th>
                        <th>Description</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(paginatedTransactions) && paginatedTransactions.length > 0 ? (
                        paginatedTransactions.map((tx) => (
                          <tr key={tx.id}>
                          <td>{new Date(tx.updated_at).toLocaleDateString()}</td>
                          <td>{getItemCode(tx.item_id)}</td>
                          <td>{getVillaName(tx.villa_id)}</td>
                          <td>{tx.quantity}</td>
                          <td>Rp. {tx.price_per_unit.toLocaleString("id-ID")}</td>
                          <td>{tx.supplier_name || "-"}</td>
                          <td>{tx.transaction_type}</td>
                          <td className="cell-description">{tx.description || "-"}</td>
                          <td>
                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-sm btn-icon text-primary"
                                onClick={() => handleEdit(tx)}
                                title="Edit Transaction"
                              >
                                <i className="bx bx-edit"></i>
                              </button>
                              {/* <button
                                className="btn btn-sm btn-icon text-danger"
                                onClick={() => handleDelete(tx.id)}
                                title="Delete Transaction"
                              >
                                <i className="bx bx-trash"></i>
                              </button> */}
                            </div>
                          </td>
                        </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9" className="text-center">No transactions found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  <div className="d-flex justify-content-between align-items-center mt-3 px-2">
                    <small>
                      Showing {filteredTransactions.length === 0 ? 0 : startIndex} to {endIndex} of {filteredTransactions.length} transactions
                    </small>
                    <small>Page {currentPage} of {totalPages}</small>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage((prev) => prev - 1)}
                      >
                        Previous
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage((prev) => prev + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                  <div className="d-flex justify-content-end mt-3 px-2 gap-2">
                    <button className="btn btn-primary" onClick={() => exportToXLSX(filteredTransactions, items)}>
                      <i className="bx bx-table"></i> Export to XLSX
                    </button>
                  </div>
                  <button className="fab" onClick={() => setShowCreateModal(true)}>
                    <span className="icon">+</span>
                    <span className="label">Create Transaction</span>
                  </button>

                  <CreateStorageTransactionModal
                    show={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={fetchTransactions}
                  />
                  <EditStorageTransactionModal
                    show={showEditModal}
                    onClose={() => setShowEditModal(false)}
                    transaction={selectedTransaction}
                    onSuccess={fetchTransactions}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
